/**
 * Based on three.js SAOPass.js
 * @author ludobaka / ludobaka.github.io
 * SAO implementation inspired from bhouston previous SAO work
 * Modifications copyright Higharc Inc. 2019
 */
import * as THREE from "three";
import type { AOPassBlurParams } from "./AOPass";
import { AOPass } from "./AOPass";
import { DepthLimitedBlurShader2 } from "./DepthLimitedBlurShader2";
import type { DepthNormalPass } from "./DepthNormalPass";
import type { DepthTextureType } from "./DepthTextureType";
import { SAOShader2 } from "./SAOShader2";
import { screenSpaceViewNormalPrepareShader, screenSpaceViewPositionPrepareShader } from "./screenSpaceHelpersGLSL";
import { SSFXRendererOutput } from "./SSFXRendererOutput";

// It doesn't necessarily make sense to use every mip level in the shader. This stride value controls how many depth mip levels are picked for the shader.
const DEPTH_MIP_STRIDE = 2;

interface SAOMaterialSet {
  saoMaterial: THREE.ShaderMaterial;
}

function initMaterialSet(isPerspectiveCamera: boolean): SAOMaterialSet {
  const saoMaterial = new THREE.ShaderMaterial({
    defines: Object.assign({}, SAOShader2.defines),
    fragmentShader: SAOShader2.fragmentShader,
    vertexShader: SAOShader2.vertexShader,
    uniforms: THREE.UniformsUtils.clone(SAOShader2.uniforms),
    blending: THREE.NoBlending,
    depthTest: false,
    depthWrite: false,
    name: "SAOPass2 saoMaterial",
  });
  saoMaterial.name = "SAO";

  if (!isPerspectiveCamera) {
    saoMaterial.defines["PERSPECTIVE_CAMERA"] = 0;
  }

  return {
    saoMaterial,
  };
}

export interface SAOPass2Params extends AOPassBlurParams {
  saoBias: number;
  aoIntensity: number;
  saoScale: number;
  worldSpaceScale: number;
  maxScreenSpaceKernelRadius: number;
  saoMinResolution: number;

  assumedThickness: number;

  aoResolutionMultiplier: number;

  numSamples: number;
  blurNormalCutoffEnabled: boolean;
  depthCutoffBasedOnNormal: boolean;
  useNormalTextureInSAO: boolean;

  // If this is on, SAO assumes that geometry is thick in the camera forward direction.
  // This can create unrealistic haloing around objects that are actually thin,
  // but renders better results for walls displayed from a top-down orthogonal camera for example.
  assumeThickGeometry: boolean;

  // Normal sampling region is sphere shaped.
  // Simplified sampling region is rectangular prism shaped.
  // The rectangular prism is cheaper to evaluate but is more likely jarring transitions when geometry moves across
  // the edge of the sampling region.
  simplifiedSamplingRegion: boolean;
}

export const defaultSAOPass2Params: SAOPass2Params = {
  saoBias: 0.22,
  aoIntensity: 1.0,
  saoScale: 0.8,
  worldSpaceScale: 16.0,
  maxScreenSpaceKernelRadius: 0.1,
  saoMinResolution: 1.1,
  ssaoBlur: true,

  blurKernelSamples: 6, // number of blur samples taken is double this value
  blurStdDev: 3,
  blurDepthCutoff: 4.0,
  blurNormalAngleCutoff: Math.PI * 0.3,

  assumedThickness: 0.0,

  aoResolutionMultiplier: 0.5,

  numSamples: SAOShader2.defines.NUM_SAMPLES,
  blurNormalCutoffEnabled: DepthLimitedBlurShader2.defines.NORMAL_CUTOFF === 1,
  depthCutoffBasedOnNormal: DepthLimitedBlurShader2.defines.DEPTH_CUTOFF_BASED_ON_NORMAL === 1,
  useNormalTextureInSAO: true,
  useNormalTextureInBlur: false,
  output: SSFXRendererOutput.AO,

  assumeThickGeometry: false,

  simplifiedSamplingRegion: false,

  usePoissonBlur: true,
};

export class SAOPass2 extends AOPass {
  readonly params: SAOPass2Params;

  private depthTextureTypeInternal: DepthTextureType;
  get depthTextureType(): DepthTextureType {
    return this.depthTextureTypeInternal;
  }
  set depthTextureType(value: DepthTextureType) {
    if (value === this.depthTextureTypeInternal) {
      return;
    }
    this.depthTextureTypeInternal = value;
    this.refreshSAOShaders();
  }
  private useNormalTextureInSAO: boolean = defaultSAOPass2Params.useNormalTextureInSAO;
  private useNormalTextureInBlur: boolean = defaultSAOPass2Params.useNormalTextureInBlur;
  private usePoissonBlur: boolean = defaultSAOPass2Params.usePoissonBlur;

  private assumeThickGeometry: boolean = defaultSAOPass2Params.assumeThickGeometry;
  private simplifiedSamplingRegion: boolean = defaultSAOPass2Params.simplifiedSamplingRegion;

  private output: SSFXRendererOutput = defaultSAOPass2Params.output;

  private perspectiveMaterials: SAOMaterialSet;
  private orthographicMaterials: SAOMaterialSet;

  constructor(camera: THREE.Camera, depthNormalPass: DepthNormalPass, size?: THREE.Vector2) {
    super(camera, depthNormalPass, defaultSAOPass2Params.aoResolutionMultiplier, size);

    this.depthTextureTypeInternal = depthNormalPass.depthTextureType;

    const scope = this; // eslint-disable-line @typescript-eslint/no-this-alias

    this.params = {
      ...defaultSAOPass2Params,

      set aoResolutionMultiplier(value) {
        scope.aoResolutionMultiplier = value;
        scope.refreshResolution();
      },
      get aoResolutionMultiplier() {
        return scope.aoResolutionMultiplier;
      },

      // Note that odd numSamples values work better than even ones.
      set numSamples(value: number) {
        if (value === scope.perspectiveMaterials.saoMaterial.defines["NUM_SAMPLES"]) {
          return;
        }
        scope.perspectiveMaterials.saoMaterial.defines["NUM_SAMPLES"] = value;
        scope.orthographicMaterials.saoMaterial.defines["NUM_SAMPLES"] = value;
        let numRings = 4;
        if (value < 7) {
          numRings = 2;
        }
        if (value < 5) {
          numRings = 1;
        }
        scope.perspectiveMaterials.saoMaterial.defines["NUM_RINGS"] = numRings;
        scope.orthographicMaterials.saoMaterial.defines["NUM_RINGS"] = numRings;
        scope.perspectiveMaterials.saoMaterial.needsUpdate = true;
        scope.orthographicMaterials.saoMaterial.needsUpdate = true;
      },
      get numSamples(): number {
        return scope.perspectiveMaterials.saoMaterial.defines["NUM_SAMPLES"] as number;
      },

      // This value toggles whether normal discontinuities trigger the blur cutoff.
      set blurNormalCutoffEnabled(value) {
        if (scope.perspectiveBlur.params.normalCutoffEnabled === value) {
          return;
        }
        scope.perspectiveBlur.params.normalCutoffEnabled = value;
        scope.orthographicBlur.params.normalCutoffEnabled = value;
        scope.perspectiveBlurNormalMap.params.normalCutoffEnabled = value;
        scope.orthographicBlurNormalMap.params.normalCutoffEnabled = value;
      },
      get blurNormalCutoffEnabled() {
        return scope.perspectiveBlur.params.normalCutoffEnabled;
      },

      // This value toggles whether normal information is taken advantage of when checking depth discontinuities when blurring.
      set depthCutoffBasedOnNormal(value) {
        if (scope.perspectiveBlur.params.depthCutoffBasedOnNormal === value) {
          return;
        }
        scope.perspectiveBlur.params.depthCutoffBasedOnNormal = value;
        scope.orthographicBlur.params.depthCutoffBasedOnNormal = value;
        scope.perspectiveBlurNormalMap.params.depthCutoffBasedOnNormal = value;
        scope.orthographicBlurNormalMap.params.depthCutoffBasedOnNormal = value;
      },
      get depthCutoffBasedOnNormal() {
        return scope.perspectiveBlur.params.depthCutoffBasedOnNormal;
      },

      set useNormalTextureInSAO(value) {
        if (value === scope.useNormalTextureInSAO) {
          return;
        }
        scope.useNormalTextureInSAO = value;
        scope.refreshSAOShaders();
      },
      get useNormalTextureInSAO() {
        return scope.useNormalTextureInSAO;
      },

      // This value toggles whether normals are inferred from depth or if they're sampled from the normal texture
      // in the blur step.
      set useNormalTextureInBlur(value) {
        scope.useNormalTextureInBlur = value;
      },
      get useNormalTextureInBlur() {
        return scope.useNormalTextureInBlur;
      },

      set output(value) {
        if (value === scope.output) {
          return;
        }
        scope.output = value;
        scope.refreshSAOShaders();
      },
      get output() {
        return scope.output;
      },

      set assumeThickGeometry(value) {
        if (value === scope.assumeThickGeometry) {
          return;
        }
        scope.assumeThickGeometry = value;
        scope.refreshSAOShaders();
      },
      get assumeThickGeometry() {
        return scope.assumeThickGeometry;
      },

      set simplifiedSamplingRegion(value) {
        if (value === scope.simplifiedSamplingRegion) {
          return;
        }
        scope.simplifiedSamplingRegion = value;
        scope.refreshSAOShaders();
      },
      get simplifiedSamplingRegion() {
        return scope.simplifiedSamplingRegion;
      },

      set usePoissonBlur(value) {
        scope.usePoissonBlur = value;
      },
      get usePoissonBlur() {
        return scope.usePoissonBlur;
      },
    };

    this.perspectiveMaterials = initMaterialSet(true);
    this.orthographicMaterials = initMaterialSet(false);
    this.refreshSAOShaders();
  }

  get screenSpaceAoMap(): THREE.Texture {
    return this.params.ssaoBlur ? this.blurFinalRenderTarget.texture : this.aoRenderTarget.texture;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    this.clearSettingsSaver.saveClearSettings(renderer);
    renderer.autoClear = false;

    this.renderSAO(renderer);
    this.blurAO(renderer, this.params);

    this.renderOutput(renderer, this.params.output, writeBuffer);

    this.clearSettingsSaver.restoreClearSettings(renderer);
  }

  renderSAO(renderer: THREE.WebGLRenderer) {
    const materialCollection = (this.camera as THREE.PerspectiveCamera).isPerspectiveCamera
      ? this.perspectiveMaterials
      : this.orthographicMaterials;
    const saoMaterial = materialCollection.saoMaterial;

    screenSpaceViewPositionPrepareShader(saoMaterial, this.camera, this.depthNormalPass, {
      skipSettingDepthBuffer: true,
    });

    const depthTextures = [
      this.depthNormalPass.params.enableViewZConversion
        ? this.depthNormalPass.viewZTexture
        : this.depthNormalPass.depthTexture,
    ];
    let downsampledDepthIndex = DEPTH_MIP_STRIDE - 1;
    const depthMips = this.depthMipsForSAOShader();
    while (depthTextures.length < depthMips) {
      depthTextures.push(this.depthNormalPass.getDepthMip(downsampledDepthIndex));
      downsampledDepthIndex += DEPTH_MIP_STRIDE;
    }
    saoMaterial.uniforms["tDepth"].value = depthTextures;

    saoMaterial.uniforms["bias"].value = this.params.saoBias;
    saoMaterial.uniforms["intensity"].value = this.params.aoIntensity;
    saoMaterial.uniforms["scale"].value = this.params.saoScale;
    saoMaterial.uniforms["worldSpaceScale"].value = this.params.worldSpaceScale;
    saoMaterial.uniforms["maxScreenSpaceKernelRadius"].value = this.params.maxScreenSpaceKernelRadius;
    saoMaterial.uniforms["minResolution"].value = this.params.saoMinResolution;
    saoMaterial.uniforms["assumedThickness"].value = this.params.assumedThickness;

    saoMaterial.uniforms["randomSeed"].value = this.randomSeed;

    // Rendering SAO texture
    const saoRenderTarget = this.getAoRenderTarget(this.params.output);
    this.renderPass(renderer, saoMaterial, saoRenderTarget, 0xffffff, 1.0);
  }

  private depthMipsForSAOShader() {
    return Math.floor(this.depthNormalPass.params.downsampleDepthSteps / DEPTH_MIP_STRIDE) + 1;
  }

  private refreshSAOShader(materialCollection: SAOMaterialSet) {
    const material = materialCollection.saoMaterial;
    // Set defines
    material.defines["DEPTH_PACKING"] = this.depthNormalPass.isDepthRGBAPacked() ? 1 : 0;
    material.defines["NORMAL_TEXTURE"] = this.useNormalTextureInSAO ? 1 : 0;
    screenSpaceViewNormalPrepareShader(material, this.depthNormalPass);

    const writeNormalsFromShader = this.params.output === SSFXRendererOutput.NORMAL_FOR_AO;
    material.defines["OUTPUT_NORMALS"] = writeNormalsFromShader ? 1 : 0;
    material.defines["ASSUME_THICK_GEOMETRY"] = this.assumeThickGeometry ? 1 : 0;
    material.defines["SIMPLIFIED_SAMPLING_REGION"] = this.simplifiedSamplingRegion ? 1 : 0;
    material.needsUpdate = true;

    material.defines["DEPTH_MIPS"] = this.depthMipsForSAOShader();

    // Set uniforms that don't need to be set on every frame.
    material.uniforms["invSize"].value.set(1.0 / this.paddedSize.x, 1.0 / this.paddedSize.y);
  }

  private refreshSAOShaders() {
    this.refreshSAOShader(this.perspectiveMaterials);
    this.refreshSAOShader(this.orthographicMaterials);
  }

  needsNormalTexture() {
    return this.useNormalTextureInSAO || this.useNormalTextureInBlur || this.usePoissonBlur;
  }

  private refreshResolutionInMaterialSet(materialSet: SAOMaterialSet) {
    materialSet.saoMaterial.uniforms["invSize"].value.set(1.0 / this.paddedSize.x, 1.0 / this.paddedSize.y);
  }

  protected refreshResolution() {
    super.refreshResolution();
    this.refreshResolutionInMaterialSet(this.perspectiveMaterials);
    this.refreshResolutionInMaterialSet(this.orthographicMaterials);
  }

  dispose() {
    super.dispose();
    const materialSets = [this.perspectiveMaterials, this.orthographicMaterials];
    for (let i = 0; i < 2; ++i) {
      materialSets[i].saoMaterial.dispose();
    }
  }
}
